import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  getFinancialPeriodDetails,
  updateFinancialPeriod,
  clearError,
  clearSuccess,
  clearFinancialPeriodDetails,
} from '../../../../redux/Slices/authSlice';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import { useTitle } from '../../../../shared/hooks/TitleContext';

const statusOptions = ['Open', 'Closed'];

const EditFinancialPeriod = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { id } = useParams();
  const { financialPeriodDetails, isLoading, error, success } = useSelector((state) => state.auth);

  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  useEffect(() => {
    setTitle('financialPeriods.edit');
  }, [setTitle]);

  const [formData, setFormData] = useState(null);

  useEffect(() => {
    if (id) {
      dispatch(getFinancialPeriodDetails(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (financialPeriodDetails) {
      setFormData({
        name: financialPeriodDetails.name || '',
        start_date: financialPeriodDetails.start_date || '',
        end_date: financialPeriodDetails.end_date || '',
        status: financialPeriodDetails.status || 'Open',
      });
    }
  }, [financialPeriodDetails]);

  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, {
          rtl: isRTL,
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastSuccessRef.current = { message: success, time: now };
      }
      setTimeout(() => {
        dispatch(clearSuccess());
        navigate('/financial-periods');
      }, 1500);
    }
  }, [success, dispatch, navigate, isRTL]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, {
          rtl: isRTL,
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData) return;
    if (!formData.name || !formData.start_date || !formData.end_date) {
      toast.error(t('financialPeriods.form.allFieldsRequired', { defaultValue: 'Please fill in all required fields' }), { rtl: isRTL });
      return;
    }
    dispatch(updateFinancialPeriod({ id, data: formData }));
  };

  if (!formData) {
    return (
      <div
        style={{
          padding: '30px',
          backgroundColor: '#14263b',
          minHeight: 'calc(100vh - 80px)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          direction: dir
        }}
      >
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        direction: dir
      }}
    >
      <div style={{ marginBottom: '30px', maxWidth: '900px', marginInline: 'auto' }}>
        <button
          onClick={() => navigate('/financial-periods')}
          style={{
            backgroundColor: '#666',
            color: 'white',
            border: 'none',
            padding: '10px',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            flexDirection: isRTL ? 'row' : 'row-reverse'
          }}
        >
          <FaArrowRight style={{ transform: isRTL ? 'rotate(180deg)' : 'none' }} />
          {t('common.back')}
        </button>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, textAlign }}>{t('financialPeriods.edit')}</h1>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          backgroundColor: '#1b3350',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '32px',
          width: '100%',
          maxWidth: '900px',
          margin: '0 auto',
          boxSizing: 'border-box',
          direction: dir
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'white', textAlign, direction: dir }}>
              {t('financialPeriods.form.name')} <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                boxSizing: 'border-box',
                direction: dir,
                textAlign
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'white', textAlign, direction: dir }}>
                {t('financialPeriods.form.startDate')} <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  direction: dir
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'white', textAlign, direction: dir }}>
                {t('financialPeriods.form.endDate', { defaultValue: 'End Date' })} <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  direction: dir
                }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: 'white', textAlign, direction: dir }}>
              {t('financialPeriods.form.status')}
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                boxSizing: 'border-box',
                cursor: 'pointer',
                direction: dir,
                textAlign
              }}
            >
              {statusOptions.map((option) => (
                <option key={option} value={option}>
                  {t(`financialPeriods.status.${option.toLowerCase()}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexDirection: isRTL ? 'row' : 'row-reverse' }}>
          <button
            type="button"
            onClick={() => navigate('/financial-periods')}
            style={{
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 16px',
              cursor: 'pointer',
            }}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              backgroundColor: '#AC2000',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 16px',
              cursor: 'pointer',
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            {isLoading ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditFinancialPeriod;


